import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";
import MediaUploader from "../../components/admin/MediaUploader";

type CertificateItem = {
  id: number;
  titleKey: string;
  organizationKey: string;
  descriptionKey: string;
  categoryKey: string;
  statusKey: string;
  documentNo: string;
  issueDate: string;
  validUntil: string;
  fileSize: string;
  preview?: string;
  file?: string;
};

const categoryKeys = [
  "certificates.categories.fireSafety",
  "certificates.categories.qualityManagement",
  "certificates.categories.europeanStandard",
  "certificates.categories.testStandard",
  "certificates.categories.qualityAssurance",
  "certificates.categories.environmentalManagement",
];

const statusKeys = [
  "certificates.status.active",
  "certificates.status.renewing",
];

const CertificatesAdmin: React.FC = () => {
  const [items, setItems] = React.useState<CertificateItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [draft, setDraft] = React.useState<Partial<CertificateItem>>({});
  const [editIndex, setEditIndex] = React.useState<number | null>(null);
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState(
    "chore(certificates): update"
  );
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  const previewBranchRef = React.useRef<string>("");

  const getActiveBranch = () => {
    if (publishMode !== "preview") return undefined;
    const normalized = branchName.trim();
    if (normalized) return normalized;
    if (!previewBranchRef.current) {
      previewBranchRef.current = `preview/certificates-${Date.now()}`;
    }
    return previewBranchRef.current;
  };

  const activeBranch = getActiveBranch();

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/data/certificates/index.json", {
        cache: "no-store",
      });
      const json = await res.json();
      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (e: any) {
      setError(e?.message || "Sertifikalar okunamadı");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const saveAll = async (next: CertificateItem[]) => {
    setLoading(true);
    setError("");
    try {
      const branch = activeBranch;

      await contentWrite(
        "data/certificates/index.json",
        JSON.stringify({ items: next }, null, 2),
        `${commitMsg}`,
        branch
      );
      if (branch) {
        const pr = await createPreviewPR(branch, "Certificates Update");
        setPrUrl(pr.url);
      }
      setItems(next);
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const add = () => {
    if (!draft.titleKey?.trim()) return setError("titleKey gerekli");
    if (!draft.organizationKey?.trim())
      return setError("organizationKey gerekli");
    if (!draft.descriptionKey?.trim())
      return setError("descriptionKey gerekli");
    if (
      draft.validUntil &&
      draft.issueDate &&
      draft.validUntil < draft.issueDate
    )
      return setError("Geçerlilik tarihi veriliş tarihinden önce olamaz");
    const nextId = items.length ? Math.max(...items.map((i) => i.id)) + 1 : 1;
    const base: CertificateItem = {
      id: nextId,
      titleKey: draft.titleKey || "certificatesItems.new.title",
      organizationKey:
        draft.organizationKey || "certificatesItems.new.organization",
      descriptionKey:
        draft.descriptionKey || "certificatesItems.new.description",
      categoryKey: draft.categoryKey || categoryKeys[0],
      statusKey: draft.statusKey || statusKeys[0],
      documentNo: draft.documentNo || "",
      issueDate: draft.issueDate || new Date().toISOString().slice(0, 10),
      validUntil: draft.validUntil || new Date().toISOString().slice(0, 10),
      fileSize: draft.fileSize || "",
      preview: draft.preview,
      file: draft.file,
    };
    setItems([...items, base]);
    setDraft({});
  };

  const beginEdit = (idx: number) => {
    setEditIndex(idx);
    setDraft(items[idx]);
  };

  const commitEdit = () => {
    if (editIndex === null) return;
    if (!draft.titleKey?.trim()) return setError("titleKey gerekli");
    if (!draft.organizationKey?.trim())
      return setError("organizationKey gerekli");
    if (!draft.descriptionKey?.trim())
      return setError("descriptionKey gerekli");
    if (
      draft.validUntil &&
      draft.issueDate &&
      draft.validUntil < draft.issueDate
    )
      return setError("Geçerlilik tarihi veriliş tarihinden önce olamaz");
    const updated = {
      ...(items[editIndex] as CertificateItem),
      ...(draft as any),
    };
    const next = items.map((it, i) => (i === editIndex ? updated : it));
    setItems(next);
    setEditIndex(null);
    setDraft({});
  };

  const remove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
  };

  const publish = async () => {
    if (!items.length) {
      // Yayına alınacak sertifika yoksa sessiz geç
    }
    await saveAll(items);
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Sertifika Yönetimi
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: Anahtar alanlar i18n çeviri anahtarlarıdır (ör.
          certificates.status.active). Önizleme ve dosyayı yükledikten sonra
          commit mesajı yazarak Prod/PR olarak kaydedebilirsiniz.
        </Alert>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/certificates`}
          />
          <Button
            variant="contained"
            onClick={publish}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        {/* Add new */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Yeni Sertifika
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="titleKey"
                  fullWidth
                  value={draft.titleKey || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, titleKey: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="organizationKey"
                  fullWidth
                  value={draft.organizationKey || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, organizationKey: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="descriptionKey"
                  fullWidth
                  value={draft.descriptionKey || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, descriptionKey: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  label="categoryKey"
                  fullWidth
                  value={draft.categoryKey || categoryKeys[0]}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, categoryKey: e.target.value }))
                  }
                >
                  {categoryKeys.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  label="statusKey"
                  fullWidth
                  value={draft.statusKey || statusKeys[0]}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, statusKey: e.target.value }))
                  }
                >
                  {statusKeys.map((k) => (
                    <MenuItem key={k} value={k}>
                      {k}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="documentNo"
                  fullWidth
                  value={draft.documentNo || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, documentNo: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="issueDate"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={draft.issueDate || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, issueDate: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="validUntil"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={draft.validUntil || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, validUntil: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="fileSize"
                  fullWidth
                  value={draft.fileSize || ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, fileSize: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MediaUploader
                  title="Önizleme (PDF/Resim)"
                  accept="application/pdf,image/*"
                  multiple={false}
                  destPrefix={`data/certificates/uploads`}
                  branch={activeBranch}
                  onUploaded={(paths) =>
                    setDraft((d) => ({ ...d, preview: paths[0] }))
                  }
                />
                {draft.preview && (
                  <Chip label={draft.preview.split("/").pop()} sx={{ mt: 1 }} />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <MediaUploader
                  title="Dosya (PDF)"
                  accept="application/pdf"
                  multiple={false}
                  destPrefix={`data/certificates/uploads`}
                  branch={activeBranch}
                  onUploaded={(paths) =>
                    setDraft((d) => ({ ...d, file: paths[0] }))
                  }
                />
                {draft.file && (
                  <Chip label={draft.file.split("/").pop()} sx={{ mt: 1 }} />
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={add}
                  disabled={loading}
                >
                  Ekle
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* List */}
        <Grid container spacing={2}>
          {items.map((it, idx) => (
            <Grid item xs={12} key={it.id}>
              <Card>
                <CardContent>
                  {editIndex === idx ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="titleKey"
                          fullWidth
                          value={draft.titleKey || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              titleKey: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="organizationKey"
                          fullWidth
                          value={draft.organizationKey || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              organizationKey: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="descriptionKey"
                          fullWidth
                          value={draft.descriptionKey || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              descriptionKey: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          label="categoryKey"
                          fullWidth
                          value={draft.categoryKey || categoryKeys[0]}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              categoryKey: e.target.value,
                            }))
                          }
                        >
                          {categoryKeys.map((k) => (
                            <MenuItem key={k} value={k}>
                              {k}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          label="statusKey"
                          fullWidth
                          value={draft.statusKey || statusKeys[0]}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              statusKey: e.target.value,
                            }))
                          }
                        >
                          {statusKeys.map((k) => (
                            <MenuItem key={k} value={k}>
                              {k}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="documentNo"
                          fullWidth
                          value={draft.documentNo || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              documentNo: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="issueDate"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={draft.issueDate || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              issueDate: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="validUntil"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={draft.validUntil || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              validUntil: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label="fileSize"
                          fullWidth
                          value={draft.fileSize || ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              fileSize: e.target.value,
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <MediaUploader
                          title="Önizleme"
                          accept="application/pdf,image/*"
                          multiple={false}
                          destPrefix={`data/certificates/uploads`}
                          branch={activeBranch}
                          onUploaded={(paths) =>
                            setDraft((d) => ({ ...d, preview: paths[0] }))
                          }
                        />
                        {draft.preview && (
                          <Chip
                            label={draft.preview.split("/").pop()}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <MediaUploader
                          title="Dosya"
                          accept="application/pdf"
                          multiple={false}
                          destPrefix={`data/certificates/uploads`}
                          branch={activeBranch}
                          onUploaded={(paths) =>
                            setDraft((d) => ({ ...d, file: paths[0] }))
                          }
                        />
                        {draft.file && (
                          <Chip
                            label={draft.file.split("/").pop()}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            startIcon={<SaveIcon />}
                            variant="contained"
                            onClick={commitEdit}
                            disabled={loading}
                          >
                            Kaydet
                          </Button>
                          <Button
                            onClick={() => {
                              setEditIndex(null);
                              setDraft({});
                            }}
                          >
                            Vazgeç
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  ) : (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems="center"
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          #{it.id} {it.titleKey}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {it.organizationKey} • {it.categoryKey} •{" "}
                          {it.statusKey}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => beginEdit(idx)}
                        >
                          Düzenle
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => remove(idx)}
                        >
                          Sil
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CertificatesAdmin;
